import { UltrarubigramPartialState, UltrarubigramPieceOrientation } from '../../../utils/types';
/**
 * The puzzle as a tree of square pieces, each with its own orientation.
 *
 *   puzzle (24x24, rotates) > 4 grids (12x12) > 4 quadrants (6x6) > 4 subgrids (3x3) > 9 cells
 *
 * A piece's orientation is an element of the dihedral group D4: one of four rotations,
 * optionally mirrored. Its orientation on screen is its parent's screen orientation composed
 * with its own local one, and the position of anything inside it is found by applying that
 * map to the thing's offset from the piece's center. That is enough to answer every question
 * the UI has: which letter is where (for checking the solution), which way a letter must be
 * counter-rotated to read upright, which way a click on a side cell should flip, and which
 * orientations would solve the puzzle (for the reveal animation).
 *
 * Distances are in half-cells so that every center and offset is an integer. The puzzle is 48
 * half-cells wide, grid centers sit at ±12 from the puzzle center, quadrant centers at ±6 from
 * their grid's center, subgrid centers at ±3 from their quadrant's, and cell centers at
 * -2, 0, 2 from their subgrid's.
 *
 * Screen coordinates: x to the right, y down. A positive CSS rotation is clockwise, which in
 * these coordinates maps (1, 0) to (0, 1).
 */

export type Orientation = {
  /** Degrees, a multiple of 90, cumulative so CSS transitions keep turning the same way. */
  rot: number;
  flipH: boolean;
  flipV: boolean;
};

/** A D4 element: x' = a*x + b*y, y' = c*x + d*y. */
type Matrix = [number, number, number, number];

const rotationMatrix = (quarterTurns: number): Matrix => {
  const cos = [1, 0, -1, 0][quarterTurns];
  const sin = [0, 1, 0, -1][quarterTurns];
  return [cos, -sin, sin, cos];
};

/** Elements 0..3 are rotations by 0/90/180/270 degrees; 4..7 are those rotations mirrored. */
const MATRICES: Matrix[] = [];
for (let k = 0; k < 4; k++) MATRICES.push(rotationMatrix(k));
for (let k = 0; k < 4; k++) {
  const [a, b, c, d] = rotationMatrix(k);
  MATRICES.push([-a, -b, c, d]); // reflect x after rotating
}

const matrixIndex = ([a, b, c, d]: Matrix): number => {
  for (let i = 0; i < 8; i++) {
    const m = MATRICES[i];
    if (m[0] === a && m[1] === b && m[2] === c && m[3] === d) return i;
  }
  throw new Error('not a D4 element');
};

const MUL: number[][] = [];
const INV: number[] = [];
for (let i = 0; i < 8; i++) {
  MUL.push([]);
  const [a, b, c, d] = MATRICES[i];
  for (let j = 0; j < 8; j++) {
    const [e, f, g, h] = MATRICES[j];
    MUL[i].push(matrixIndex([a * e + b * g, a * f + b * h, c * e + d * g, c * f + d * h]));
  }
  INV.push(matrixIndex([a, c, b, d])); // orthogonal, so the inverse is the transpose
}

export const IDENTITY = 0;
/** Mirror left-to-right (what rotateY(180deg) does to a flat element). */
export const FLIP_X = 4;
/** Mirror top-to-bottom (rotateX(180deg)). */
export const FLIP_Y = 6;

/** first then second: the element that applies `inner` and then `outer`. */
export const compose = (outer: number, inner: number): number => MUL[outer][inner];
export const inverse = (el: number): number => INV[el];
export const isMirrored = (el: number): boolean => el >= 4;
/** True when the element swaps the horizontal and vertical axes. */
export const isTurned = (el: number): boolean => MATRICES[el][0] === 0;

export const apply = (el: number, x: number, y: number): [number, number] => {
  const [a, b, c, d] = MATRICES[el];
  return [a * x + b * y, c * x + d * y];
};

export const rotationElement = (degrees: number): number => (((degrees / 90) % 4) + 4) % 4;

/**
 * The element a piece's CSS transform `rotateY(h) rotateX(v) rotateZ(rot)` amounts to in the
 * plane. CSS applies the rightmost function first.
 */
export const orientationElement = ({ rot, flipH, flipV }: Orientation): number => {
  let el = rotationElement(rot);
  if (flipV) el = compose(FLIP_Y, el);
  if (flipH) el = compose(FLIP_X, el);
  return el;
};

/**
 * Expresses an element as CSS `rotate(angle) scaleX(flip ? -1 : 1)`, i.e. mirror first, then
 * turn. Angles come back in 0..270; see continueAngle for keeping transitions short.
 */
export const toCss = (el: number): { angle: number; flip: boolean } => {
  const flip = isMirrored(el);
  const rotation = flip ? compose(el, FLIP_X) : el;
  return { angle: rotation * 90, flip };
};

/**
 * The angle congruent to `target` (mod 360) that is nearest to `previous`, so a 90-degree turn
 * transitions as a 90-degree turn and never as a 270-degree one the other way.
 */
export const continueAngle = (previous: number, target: number): number => {
  const diff = ((((target - previous) % 360) + 540) % 360) - 180;
  return previous + diff;
};

// ---------------------------------------------------------------------------
// Positions
// ---------------------------------------------------------------------------

export const PUZZLE_HALF_SIZE = 24; // half-cells

/** Child index -> offset of its center from its parent's center, in units of the child's half size. */
const CORNER: [number, number][] = [[-1, -1], [1, -1], [-1, 1], [1, 1]]; // TL, TR, BL, BR

export const GRID_HALF_SIZE = 12;
export const QUADRANT_HALF_SIZE = 6;
export const SUBGRID_HALF_SIZE = 3;

export const childOffset = (index: number, halfSize: number): [number, number] => [
  CORNER[index][0] * halfSize,
  CORNER[index][1] * halfSize,
];

/** Which corner (TL=0, TR=1, BL=2, BR=3) an offset from a center points into. */
export const cornerIndex = (x: number, y: number): number => (x > 0 ? 1 : 0) + (y > 0 ? 2 : 0);

/** Offset of cell (row, col) of a 3x3 from the subgrid's center. */
export const cellOffset = (row: number, col: number): [number, number] => [(col - 1) * 2, (row - 1) * 2];

/** The four edges of a square piece, as the outward direction of each. */
export type Edge = 'top' | 'right' | 'bottom' | 'left';
export const EDGES: Edge[] = ['top', 'right', 'bottom', 'left'];
export const EDGE_NORMAL: Record<Edge, [number, number]> = {
  top: [0, -1],
  right: [1, 0],
  bottom: [0, 1],
  left: [-1, 0],
};

// ---------------------------------------------------------------------------
// The piece tree
// ---------------------------------------------------------------------------

export type MoveKind = 'flip' | 'turn';

/** Transient animation state of a piece, kept alongside its orientation. */
export type PieceMotion = {
  /** Used to hide the letters' counter-rotation while the piece is edge-on mid-flip. */
  isFlipping: boolean;
  /** Set while a flip or turn of this piece is animating; it is then drawn above everything outside it. */
  moving: MoveKind | null;
  /** Counts this piece's moves, so the end of an earlier move can't cut a later one short. */
  moves: number;
};

export type SubgridState = Orientation & PieceMotion & {
  /**
   * What this piece displays. It never changes: flips and rotations are CSS transforms, and
   * where each letter has ended up is computed from the orientations instead.
   */
  content: string[][];
};

export type QuadrantState = Orientation & PieceMotion & {
  subgrids: SubgridState[];
};

export type GridState = Orientation & PieceMotion & {
  quadrants: QuadrantState[];
};

export type PiecePath = {
  grid: number;
  quadrant?: number;
  subgrid?: number;
};

export const SIZE = 24;

const atRest = (): Orientation & PieceMotion => ({
  rot: 0, flipH: false, flipV: false, isFlipping: false, moving: null, moves: 0,
});

export const makeEmptySubgrid = (): SubgridState => ({ ...atRest(), content: [] });

export const makeEmptyGrids = (): GridState[] =>
  Array.from({ length: 4 }, () => ({
    ...atRest(),
    quadrants: Array.from({ length: 4 }, () => ({
      ...atRest(),
      subgrids: Array.from({ length: 4 }, makeEmptySubgrid),
    })),
  }));

export const extractBlock = (grid: string[][], row: number, col: number, size: number): string[][] =>
  grid.slice(row, row + size).map(r => r.slice(col, col + size));

/** Cuts a 24-row grid into the piece tree's 64 subgrids, TL/TR/BL/BR at every level. */
export const fillContent = (grids: GridState[], rows: string[][]) => {
  grids.forEach((grid, g) => {
    grid.quadrants.forEach((quadrant, q) => {
      quadrant.subgrids.forEach((subgrid, s) => {
        const row = (g >> 1) * 12 + (q >> 1) * 6 + (s >> 1) * 3;
        const col = (g & 1) * 12 + (q & 1) * 6 + (s & 1) * 3;
        subgrid.content = extractBlock(rows, row, col, 3);
      });
    });
  });
};

/** The 24x24 as currently shown, worked out from the orientations alone. */
export const renderScreenGrid = (puzzleRot: number, grids: GridState[]): string[][] => {
  const screen: string[][] = Array.from({ length: SIZE }, () => Array(SIZE).fill(' '));
  const root = rotationElement(puzzleRot);

  grids.forEach((grid, g) => {
    const gridEl = compose(root, orientationElement(grid));
    const [gx, gy] = apply(root, ...childOffset(g, GRID_HALF_SIZE));

    grid.quadrants.forEach((quadrant, q) => {
      const quadrantEl = compose(gridEl, orientationElement(quadrant));
      const [qx, qy] = apply(gridEl, ...childOffset(q, QUADRANT_HALF_SIZE));

      quadrant.subgrids.forEach((subgrid, s) => {
        const subgridEl = compose(quadrantEl, orientationElement(subgrid));
        const [sx, sy] = apply(quadrantEl, ...childOffset(s, SUBGRID_HALF_SIZE));

        subgrid.content.forEach((cells, row) => {
          cells.forEach((cell, col) => {
            const [cx, cy] = apply(subgridEl, ...cellOffset(row, col));
            const x = gx + qx + sx + cx;
            const y = gy + qy + sy + cy;
            screen[(y + PUZZLE_HALF_SIZE - 1) / 2][(x + PUZZLE_HALF_SIZE - 1) / 2] = cell;
          });
        });
      });
    });
  });

  return screen;
};

export const gridsMatch = (a: string[][], b: string[][]): boolean => {
  for (let row = 0; row < SIZE; row++) {
    for (let col = 0; col < SIZE; col++) {
      if (a[row][col] !== b[row][col]) return false;
    }
  }
  return true;
};

// ---------------------------------------------------------------------------
// Moves, as the player sees them
// ---------------------------------------------------------------------------

export type PuzzleState = {
  rot: number;
  grids: GridState[];
};

export type Piece = Orientation & PieceMotion;

export const pieceAt = (state: PuzzleState, path: PiecePath): Piece => {
  const grid = state.grids[path.grid];
  if (path.quadrant === undefined) return grid;
  const quadrant = grid.quadrants[path.quadrant];
  if (path.subgrid === undefined) return quadrant;
  return quadrant.subgrids[path.subgrid];
};

/** The screen orientation of everything a piece sits inside, i.e. its parent's screen orientation. */
export const parentElement = (state: PuzzleState, path: PiecePath): number => {
  let el = rotationElement(state.rot);
  const grid = state.grids[path.grid];
  if (path.quadrant === undefined) return el;
  el = compose(el, orientationElement(grid));
  if (path.subgrid === undefined) return el;
  return compose(el, orientationElement(grid.quadrants[path.quadrant]));
};

export const screenElement = (state: PuzzleState, path: PiecePath): number =>
  compose(parentElement(state, path), orientationElement(pieceAt(state, path)));

/** Where a piece's center is on screen, in half-cells from the puzzle center, and its half size. */
export const screenPlacement = (state: PuzzleState, path: PiecePath): { x: number; y: number; halfSize: number } => {
  const root = rotationElement(state.rot);
  let [x, y] = apply(root, ...childOffset(path.grid, GRID_HALF_SIZE));
  if (path.quadrant === undefined) return { x, y, halfSize: GRID_HALF_SIZE };

  const gridEl = compose(root, orientationElement(state.grids[path.grid]));
  const [qx, qy] = apply(gridEl, ...childOffset(path.quadrant, QUADRANT_HALF_SIZE));
  x += qx;
  y += qy;
  if (path.subgrid === undefined) return { x, y, halfSize: QUADRANT_HALF_SIZE };

  const quadrantEl = compose(gridEl, orientationElement(state.grids[path.grid].quadrants[path.quadrant]));
  const [sx, sy] = apply(quadrantEl, ...childOffset(path.subgrid, SUBGRID_HALF_SIZE));
  return { x: x + sx, y: y + sy, halfSize: SUBGRID_HALF_SIZE };
};

/**
 * Flips a piece about a screen axis: `horizontal` swaps its left and right as the player sees
 * them, otherwise its top and bottom. The piece's CSS flips about its parent's axes, so when
 * the parent is turned sideways it is the other local flip that reads correctly. Returns the
 * local flip that was toggled.
 */
export const flipPieceOnScreen = (state: PuzzleState, path: PiecePath, horizontal: boolean): 'H' | 'V' => {
  const piece = pieceAt(state, path);
  const localIsHorizontal = isTurned(parentElement(state, path)) ? !horizontal : horizontal;
  if (localIsHorizontal) piece.flipH = !piece.flipH;
  else piece.flipV = !piece.flipV;
  return localIsHorizontal ? 'H' : 'V';
};

/**
 * Turns a piece a quarter turn clockwise (or counterclockwise) as the player sees it. Its CSS
 * rotation has to run the other way whenever its own mirroring composed with its parent's is
 * a mirror image.
 */
export const rotatePieceOnScreen = (state: PuzzleState, path: PiecePath, clockwise: boolean) => {
  const piece = pieceAt(state, path);
  const mirrored = isMirrored(parentElement(state, path)) !== (piece.flipH !== piece.flipV);
  piece.rot += (clockwise ? 90 : -90) * (mirrored ? -1 : 1);
};

// ---------------------------------------------------------------------------
// Solving
// ---------------------------------------------------------------------------

export type SolvedNode = {
  /** The piece's screen orientation once the puzzle is solved. */
  el: number;
  children: SolvedNode[];
};

export type SolvedTree = {
  rootEl: number;
  grids: SolvedNode[];
};

const matchSubgrid = (content: string[][], target: string[][]): number => {
  for (let el = 0; el < 8; el++) {
    let matches = true;
    for (let row = 0; row < 3 && matches; row++) {
      for (let col = 0; col < 3; col++) {
        const [x, y] = apply(el, ...cellOffset(row, col));
        if (target[y / 2 + 1][x / 2 + 1] !== content[row][col]) {
          matches = false;
          break;
        }
      }
    }
    if (matches) return el;
  }
  return -1;
};

/**
 * Finds screen orientations for a piece and everything inside it that make it display
 * `target`, trying all eight for the piece itself and recursing into wherever each child lands.
 */
const matchPiece = (
  children: (QuadrantState | SubgridState)[],
  childHalfSize: number,
  target: string[][],
): SolvedNode | null => {
  for (let el = 0; el < 8; el++) {
    const solvedChildren: SolvedNode[] = [];
    for (let k = 0; k < 4; k++) {
      const [x, y] = apply(el, ...childOffset(k, childHalfSize));
      const landing = cornerIndex(x, y);
      const size = childHalfSize;
      const childTarget = extractBlock(target, (landing >> 1) * size, (landing & 1) * size, size);
      const child = children[k];
      const solvedChild: SolvedNode | null = 'subgrids' in child
        ? matchPiece(child.subgrids, SUBGRID_HALF_SIZE, childTarget)
        : (() => {
          const childEl = matchSubgrid(child.content, childTarget);
          return childEl === -1 ? null : { el: childEl, children: [] };
        })();
      if (!solvedChild) break;
      solvedChildren.push(solvedChild);
    }
    if (solvedChildren.length === 4) return { el, children: solvedChildren };
  }
  return null;
};

/** Screen orientations for every piece that display `solution`, or null if none exist. */
export const solvePuzzle = (grids: GridState[], solution: string[][]): SolvedTree | null => {
  for (let rootEl = 0; rootEl < 4; rootEl++) {
    const solvedGrids: SolvedNode[] = [];
    for (let g = 0; g < 4; g++) {
      const [x, y] = apply(rootEl, ...childOffset(g, GRID_HALF_SIZE));
      const landing = cornerIndex(x, y);
      const target = extractBlock(solution, (landing >> 1) * 12, (landing & 1) * 12, 12);
      const solvedGrid = matchPiece(grids[g].quadrants, QUADRANT_HALF_SIZE, target);
      if (!solvedGrid) break;
      solvedGrids.push(solvedGrid);
    }
    if (solvedGrids.length === 4) return { rootEl, grids: solvedGrids };
  }
  return null;
};

/**
 * The flips and turn that take a piece from its current orientation to the local element
 * `target`, as the fewest animated moves: at most one flip (a mirrored target that is mirrored
 * the other way, or a double flip, is just a half turn) and then at most one turn.
 */
export const movesTo = (
  current: Orientation,
  target: number,
): { toggle: 'H' | 'V' | null; deltaRot: number } => {
  type Option = { toggle: 'H' | 'V' | null; flipH: boolean; flipV: boolean };
  const options: Option[] = [
    { toggle: null, flipH: current.flipH, flipV: current.flipV },
    { toggle: 'H', flipH: !current.flipH, flipV: current.flipV },
    { toggle: 'V', flipH: current.flipH, flipV: !current.flipV },
  ];

  let best: { toggle: 'H' | 'V' | null; deltaRot: number; cost: number } | null = null;
  for (const option of options) {
    const flipEl = orientationElement({ rot: 0, flipH: option.flipH, flipV: option.flipV });
    const rotation = compose(inverse(flipEl), target);
    if (isMirrored(rotation)) continue; // this flip combination can't reach the target
    const targetRot = rotation * 90;
    let deltaRot = (((targetRot - current.rot) % 360) + 360) % 360;
    if (deltaRot === 270) deltaRot = -90;
    const cost = (option.toggle ? 10 : 0) + Math.abs(deltaRot) / 90;
    if (!best || cost < best.cost) best = { toggle: option.toggle, deltaRot, cost };
  }
  return best!;
};

// ---------------------------------------------------------------------------
// Saving
// ---------------------------------------------------------------------------

const orientationOf = (p: Orientation): UltrarubigramPieceOrientation => ({
  rot: p.rot, flipH: p.flipH, flipV: p.flipV,
});

/** Every piece's orientation: with the puzzle data, all a mid-puzzle save needs. */
export const savedOrientations = (grids: GridState[]): UltrarubigramPartialState['grids'] =>
  grids.map(grid => ({
    ...orientationOf(grid),
    quadrants: grid.quadrants.map(quadrant => ({
      ...orientationOf(quadrant),
      subgrids: quadrant.subgrids.map(orientationOf),
    })),
  }));

const isOrientation = (o: any): o is UltrarubigramPieceOrientation =>
  !!o && typeof o === 'object' && Number.isInteger(o.rot) && o.rot % 90 === 0
  && typeof o.flipH === 'boolean' && typeof o.flipV === 'boolean';

const setOrientation = (piece: Orientation & PieceMotion, o: UltrarubigramPieceOrientation) => {
  piece.rot = o.rot;
  piece.flipH = o.flipH;
  piece.flipV = o.flipV;
  piece.isFlipping = false;
  piece.moving = null;
};

const REST: UltrarubigramPieceOrientation = { rot: 0, flipH: false, flipV: false };

/** Puts every piece back where the puzzle starts. */
export const resetOrientations = (grids: GridState[]) => {
  for (const grid of grids) {
    setOrientation(grid, REST);
    for (const quadrant of grid.quadrants) {
      setOrientation(quadrant, REST);
      for (const subgrid of quadrant.subgrids) setOrientation(subgrid, REST);
    }
  }
};

/** Applies a saved set of orientations. False, and nothing changed, if it is not the right shape. */
export const applySavedOrientations = (grids: GridState[], saved: unknown): boolean => {
  const four = (a: unknown): a is any[] => Array.isArray(a) && a.length === 4;
  const valid = four(saved) && saved.every(grid =>
    isOrientation(grid) && four((grid as any).quadrants) && (grid as any).quadrants.every((quadrant: any) =>
      isOrientation(quadrant) && four((quadrant as any).subgrids) && (quadrant as any).subgrids.every(isOrientation)));
  if (!valid) return false;

  const s = saved as UltrarubigramPartialState['grids'];
  grids.forEach((grid, g) => {
    setOrientation(grid, s[g]);
    grid.quadrants.forEach((quadrant, q) => {
      setOrientation(quadrant, s[g].quadrants[q]);
      quadrant.subgrids.forEach((subgrid, i) => setOrientation(subgrid, s[g].quadrants[q].subgrids[i]));
    });
  });
  return true;
};
